'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { initializeMetaTracking, metaUrlAllowed, revokeMetaTracking, trackMetaEvent } from '@/lib/metaPixel'
import { initAnalytics, track } from '@/lib/analytics'
import { privacyAllows, preferencePersisted, onPrivacyChange } from '@/lib/privacyRuntime'
import {
  VENDOR_PURPOSES,
  configuredVendors,
  gtagConsentFor,
  planVendorChange,
  safeIds,
  type VendorPurpose,
} from '@/lib/analyticsVendors'

// Initialisation is what is gated, not just the data: a vendor whose purpose
// is not allowed is never injected and makes no request (privacyDecision.ts):
//  • GA4 (measurement) — gtag.js loads only once measurement is allowed. Google
//    Consent Mode flags are still pushed (denied default, then an update), so
//    the ad keys follow the Marketing choice. Before 2026-10 GA4 loaded on
//    every page in advanced mode and pinged Google while "denied".
//  • Meta Pixel (advertising) — injected only once advertising is allowed.
//  • Microsoft Clarity (session_replay, the Marketing switch) — injected only
//    once allowed; clarity('consent', …) carries the explicit signal.
// Opting out mid-visit revokes each running vendor through its own API, then
// reloads once so the scripts are genuinely gone (planVendorChange).
//
// (The GCM default lives here, not in an inline head script: the repo's
// security lint blocks inline-HTML script attributes, and the default is
// order-correct as long as it is pushed before gtag.js — which it is.)

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
    fbq?: (...args: unknown[]) => void
    _fbq?: unknown
    clarity?: (...args: unknown[]) => void
    __siflyGa4Loaded?: boolean
    __siflyPixelLoaded?: boolean
    __siflyClarityLoaded?: boolean
  }
}

function loadGa4(id: string) {
  if (window.__siflyGa4Loaded) return
  window.__siflyGa4Loaded = true
  // Bootstrap the gtag stub and push GCM v2 denied defaults BEFORE gtag.js loads.
  // dataLayer is an ordered queue, so gtag.js processes 'consent default' before
  // 'config'; apply() pushes the 'consent update' for the current choice right after.
  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]) { window.dataLayer!.push(args) }
  }
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 2000,
  })
  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
  document.head.appendChild(s)
  window.gtag('js', new Date())
  window.gtag('config', id)
}

function applyGtagConsent(analytics: boolean, marketing: boolean) {
  if (typeof window.gtag !== 'function') return
  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  })
}

function loadPixel(id: string) {
  if (!metaUrlAllowed()) return
  if (window.__siflyPixelLoaded) return
  window.__siflyPixelLoaded = true
  /* eslint-disable @typescript-eslint/no-unused-expressions, prefer-spread, prefer-rest-params, @typescript-eslint/no-explicit-any -- verbatim Meta Pixel vendor bootstrap; the dynamically-attached fbq stub is untyped by the vendor snippet */
  // Standard Meta Pixel bootstrap (vendor IIFE), injected only after marketing consent.
  ;(function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return
    const n: any = (f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) })
    if (!f._fbq) f._fbq = n
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = []
    const t = b.createElement(e) as HTMLScriptElement; t.async = true; t.src = v
    const s = b.getElementsByTagName(e)[0]; s.parentNode!.insertBefore(t, s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  /* eslint-enable @typescript-eslint/no-unused-expressions, prefer-spread, prefer-rest-params, @typescript-eslint/no-explicit-any */
  window.fbq!('init', id)
  initializeMetaTracking(id)
  trackMetaEvent('PageView')
}

function loadClarity(id: string) {
  if (window.__siflyClarityLoaded) return
  window.__siflyClarityLoaded = true
  /* eslint-disable prefer-rest-params, @typescript-eslint/no-explicit-any -- verbatim MS Clarity vendor bootstrap; the dynamically-attached clarity stub is untyped by the vendor snippet */
  // Standard Microsoft Clarity bootstrap (vendor IIFE), injected only after the
  // visitor's session_replay verdict is yes. The global stub queues commands until the
  // async tag script loads, so the clarity('consent') call below is safe.
  ;(function (c: any, l: Document, a: string, r: string, i: string) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) }
    const t = l.createElement(r) as HTMLScriptElement
    t.async = true
    t.src = 'https://www.clarity.ms/tag/' + i
    const y = l.getElementsByTagName(r)[0]
    y.parentNode!.insertBefore(t, y)
  })(window, document, 'clarity', 'script', id)
  /* eslint-enable prefer-rest-params, @typescript-eslint/no-explicit-any */
  // Explicit grant — Clarity is only loaded once session_replay is allowed.
  window.clarity!('consent', true)
}

function applyClarityConsent(granted: boolean) {
  // No-op until the tag has been injected; once loaded, flip the cookie consent.
  if (typeof window.clarity !== 'function') return
  window.clarity('consent', granted)
}

export function AnalyticsScripts({
  ga4Id,
  metaPixelId,
  clarityId,
  tenantId,
}: {
  ga4Id: string | null
  metaPixelId: string | null
  clarityId: string | null
  tenantId: string
}) {
  const pathname = usePathname()
  // Track the previous pathname so we don't emit duplicate page_views on
  // the initial mount (we emit explicitly below for the first load).
  const prevPathname = useRef<string | null>(null)

  // Init the first-party analytics client once on mount (tenantId is stable per page).
  // prevPathname starts null so the pathname-change effect skips the first render.
  // We use a ref for the initial path so the dependency array is stable — this
  // effect must fire exactly once on mount, not on every navigation.
  const initialPathnameRef = useRef(pathname)
  useEffect(() => {
    initAnalytics(tenantId)
    track('page_view', { referrer: document.referrer })
    prevPathname.current = initialPathnameRef.current
  }, [tenantId])

  // Emit page_view on subsequent client-side navigations.
  useEffect(() => {
    // Skip the very first render — handled in the mount effect above.
    if (prevPathname.current === null) return
    if (prevPathname.current === pathname) return
    prevPathname.current = pathname
    track('page_view', { referrer: document.referrer }, pathname)
    trackMetaEvent('PageView')
  }, [pathname])

  useEffect(() => {
    // Re-validate IDs at the render boundary — a malformed value can never reach
    // the gtag.js URL or fbq init, however it got into the DB.
    const ids = safeIds({ ga4Id, metaPixelId, clarityId })
    const configured = configuredVendors({ ga4Id, metaPixelId, clarityId })
    const loaders: Record<VendorPurpose, () => void> = {
      measurement: () => { if (ids.measurement) loadGa4(ids.measurement) },
      advertising: () => { if (ids.advertising) loadPixel(ids.advertising) },
      session_replay: () => { if (ids.session_replay) loadClarity(ids.session_replay) },
    }
    // Each vendor is told to stop in its OWN terms first, so the change is
    // instant before the reload takes the script away.
    const revokers: Record<VendorPurpose, () => void> = {
      measurement: () => applyGtagConsent(false, false),
      advertising: () => { revokeMetaTracking(); if (typeof window.fbq === 'function') window.fbq('consent', 'revoke') },
      session_replay: () => {
        applyClarityConsent(false)
        // Also stop recording outright, in case the reload below is skipped.
        if (typeof window.clarity === 'function') window.clarity('stop')
      },
    }

    function apply() {
      const running: Record<VendorPurpose, boolean> = {
        measurement: window.__siflyGa4Loaded === true,
        advertising: window.__siflyPixelLoaded === true,
        session_replay: window.__siflyClarityLoaded === true,
      }
      const allowed = Object.fromEntries(
        VENDOR_PURPOSES.map((p) => [p, privacyAllows(p)]),
      ) as Record<VendorPurpose, boolean>
      allowed.advertising = allowed.advertising && metaUrlAllowed()
      if (allowed.advertising && running.advertising && ids.advertising) {
        window.fbq?.('consent', 'grant')
        initializeMetaTracking(ids.advertising)
      }
      const plan = planVendorChange({ running, allowed })
      for (const p of plan.load) loaders[p]()
      // Re-asserted on every pass, after any load: a Marketing opt-out on a
      // GA4-only tenant changes no running set, yet must still reach the tag.
      const gtag = gtagConsentFor({ ga4Running: window.__siflyGa4Loaded === true, allowed, configured })
      if (gtag) applyGtagConsent(gtag.analytics, gtag.advertising)
      for (const p of plan.revoke) revokers[p]()
      // Reload unless the choice never reached storage — the reload would
      // discard the only copy of it and re-inject what was just turned off.
      if (plan.reload && preferencePersisted() && metaUrlAllowed()) window.location.reload()
    }

    apply()
    return onPrivacyChange(apply)
  }, [ga4Id, metaPixelId, clarityId, tenantId, pathname])

  return null
}
